import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/requests")({
  component: LeaveRequestDataTable,
});

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

import { DateSelector } from "@/components/crud/filters/DateSelector";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import dayjs from "dayjs";
import useFetchLeaveRequests, {
  type LeaveRequestOrdering,
} from "@/hooks/api/leave/leave-request/useFetchLeaveRequests";
import useApproveLeaveRequest from "@/hooks/api/leave/leave-request/useApproveLeaveRequest";
import useFetchLeaveRequestById from "@/hooks/api/leave/leave-request/useFetchLeaveRequestById";
import useRejectLeaveRequest from "@/hooks/api/leave/leave-request/useRejectLeaveRequest";
import { LeaveRequestTable } from "@/components/crud/table/LeaveRequestTable";
import type { AdminLeaveRequestPayload, LeaveRequest } from "@/types/leave";
import { LeaveRequestDetail } from "@/components/crud/forms/LeaveRequestDetail";
import { LeaveRequestFormSheet } from "@/components/crud/forms/LeaveRequestFormSheet";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import useCreateLeaveRequest from "@/hooks/api/leave/leave-request/useCreateLeaveRequest";
import useUpdateLeaveRequest from "@/hooks/api/leave/leave-request/useUpdateLeaveRequest";
import useCancelLeaveRequest from "@/hooks/api/leave/leave-request/useCancelLeaveRequest";
import { LeaveRequestHistorySheet } from "@/components/crud/forms/LeaveRequestHistorySheet";
import { CancelWithRemarksDialog } from "@/components/crud/ConfirmDialog/CancelWithRemarksDialog";

import type { DateRange } from "react-day-picker";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import useFetchBranches from "@/hooks/api/organization/organization-branches/useFetchBranches";
import useFetchDepartments from "@/hooks/api/organization/organization-departments/useFetchDepartments";
import SelectUser from "@/components/reusable-components/select-user";
import { useDebounce } from "@/hooks/use-debounce";

import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function LeaveRequestDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canView = can(capabilities, "leave.request.view");
  const canManage = can(capabilities, "leave.request.manage");
  const canApproveOrHasSubordinates =
    can(capabilities, "leave.request.approve") ||
    Boolean(capabilities?.has_subordinates);
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link from a notification: ?leaveRequestId=<id> opens the detail sheet.
  const deepLinkId = new URLSearchParams(location.searchStr).get("leaveRequestId");
  const deepLinkRequestId = deepLinkId ? Number(deepLinkId) : null;

  const branchesQuery = useFetchBranches({ page_size: 100, is_active: true });
  const branches = branchesQuery.data?.results ?? [];

  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Filters and Search
  const { ordering, toggleOrdering, setOrdering, getApiOrdering } =
    useDataTableOrdering<
      {
        start_date: SortDirection;
        end_date: SortDirection;
        days_requested: SortDirection;
        created_at: SortDirection;
      },
      LeaveRequestOrdering
    >({
      start_date: null,
      end_date: null,
      days_requested: null,
      created_at: null,
    });
  const [statusFilter, setStatusFilter] = useState<
    "REQUESTED" | "APPROVED" | "REJECTED" | "CANCELLED" | "clear" | undefined
  >();
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>();
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>();
  const [branchFilter, setBranchFilter] = useState<string | undefined>();
  const [approverFilter, setApproverFilter] = useState<
    "stuck" | "blocked" | "clear" | undefined
  >();
  const [departmentFilter, setDepartmentFilter] = useState<
    string | undefined
  >();
  // Scoped to the chosen branch so the department list only offers real pairs.
  const departmentsQuery = useFetchDepartments({
    page_size: 100,
    branch:
      branchFilter && branchFilter !== "clear"
        ? Number(branchFilter)
        : undefined,
  });
  const departments = departmentsQuery.data?.results ?? [];
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, resetDebounce] = useDebounce(searchQuery, 500);

  // React Hook Form for user filter
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  // UI state
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState<LeaveRequest | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isCrudSheetOpen, setIsCrudSheetOpen] = useState(false);
  const [editingLeaveRequestId, setEditingLeaveRequestId] = useState<
    number | null
  >(null);
  const [selectedLeaveRequest, setSelectedLeaveRequest] =
    useState<LeaveRequest | null>(null);
  const [isHistorySheetOpen, setIsHistorySheetOpen] = useState(false);
  const [historyLeaveRequest, setHistoryLeaveRequest] =
    useState<LeaveRequest | null>(null);

  // CRUD hooks
  const leaveRequestsQuery = useFetchLeaveRequests(
    {
      page_size: pageSize,
      page,
      search: debouncedSearchQuery || undefined,
      status: statusFilter !== "clear" ? statusFilter : undefined,
      start_date: startDateFilter,
      end_date: endDateFilter,
      branch:
        branchFilter && branchFilter !== "clear"
          ? Number(branchFilter)
          : undefined,
      department:
        departmentFilter && departmentFilter !== "clear"
          ? Number(departmentFilter)
          : undefined,
      no_eligible_approver: approverFilter === "stuck" ? true : undefined,
      approval_blocked: approverFilter === "blocked" ? true : undefined,
      ordering: getApiOrdering(),
    },
    true,
    { enabled: canView },
  );
  const leaveRequests = leaveRequestsQuery.data?.results ?? [];
  const count = leaveRequestsQuery.data?.count ?? 0;

  const approveLeaveRequest = useApproveLeaveRequest();
  const rejectLeaveRequest = useRejectLeaveRequest();
  const createLeaveRequest = useCreateLeaveRequest();
  const updateLeaveRequest = useUpdateLeaveRequest();
  const cancelLeaveRequest = useCancelLeaveRequest();

  const isMutating =
    approveLeaveRequest.isPending ||
    rejectLeaveRequest.isPending ||
    createLeaveRequest.isPending ||
    updateLeaveRequest.isPending ||
    cancelLeaveRequest.isPending;
  const isLoading = (canView && leaveRequestsQuery.isPending) || isMutating;
  const isFetching = (canView && leaveRequestsQuery.isFetching) || isMutating;

  // For resetting after create, update and delete
  const resetFilters = () => {
    setOrdering({
      start_date: null,
      end_date: null,
      created_at: null,
      days_requested: null,
    });
    setStatusFilter(undefined);
    setBranchFilter(undefined);
    setDepartmentFilter(undefined);
    setDateRange(undefined);
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);
    setSearchQuery("");
    resetDebounce();
    setValue("user_id", undefined);
    setPage(1);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearchQuery,
    ordering,
    statusFilter,
    startDateFilter,
    endDateFilter,
    branchFilter,
    departmentFilter,
    approverFilter,
    setPage,
  ]);
  // Handlers

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleViewDetails = (leaveRequest: LeaveRequest) => {
    setSelectedLeaveRequest(leaveRequest);
    setIsDetailSheetOpen(true);
  };

  // Open the detail sheet for a deep-linked leave request (fetched by id).
  const deepLinkQuery = useFetchLeaveRequestById(deepLinkRequestId, true, {
    enabled: deepLinkRequestId != null,
  });
  useEffect(() => {
    if (deepLinkRequestId == null) return;
    if (deepLinkQuery.data) {
      setSelectedLeaveRequest(deepLinkQuery.data);
      setIsDetailSheetOpen(true);
    } else if (deepLinkQuery.isError) {
      // Clear the ?leaveRequestId deep-link search param.
      navigate({ to: "/admin/leave/requests", search: {} });
    }
  }, [deepLinkRequestId, deepLinkQuery.data, deepLinkQuery.isError, navigate]);

  const handleCloseDetailSheet = () => {
    setIsDetailSheetOpen(false);
    if (deepLinkRequestId != null) {
      // Clear the ?leaveRequestId deep-link search param.
      navigate({ to: "/admin/leave/requests", search: {} });
    }
  };

  const handleViewHistory = (leaveRequest: LeaveRequest) => {
    setHistoryLeaveRequest(leaveRequest);
    setIsHistorySheetOpen(true);
  };

  const handleApprove = async (id: number, remarks: string) => {
    await approveLeaveRequest.mutateAsync({ id, remarks });
    toast.success("Leave request approved successfully");
  };
  const handleReject = async (id: number, remarks: string) => {
    await rejectLeaveRequest.mutateAsync({ id, remarks });
    toast.success("Leave request rejected successfully");
  };

  // Sheet handlers
  const handleCreate = () => {
    setSelectedLeaveRequest(null);
    setEditingLeaveRequestId(null);
    setIsCrudSheetOpen(true);
  };

  const handleEdit = (leaveRequest: LeaveRequest) => {
    setSelectedLeaveRequest({ ...leaveRequest });
    setEditingLeaveRequestId(leaveRequest.id);
    setIsCrudSheetOpen(true);
  };
  const handleCancel = (leaveRequest: LeaveRequest) => {
    setLeaveToCancel(leaveRequest);
    setIsCancelDialogOpen(true);
  };

  // Cancel Confirm — an approved, not-yet-started request. Uses the non-admin
  // route (the admin viewset has no cancel action); HR is allowed there because
  // an org admin's queryset spans every user's request.
  const handleCancelConfirm = async (remarks: string) => {
    if (!leaveToCancel) return;
    try {
      await cancelLeaveRequest.mutateAsync({
        id: leaveToCancel.id,
        remarks,
      });
      toast.success("Leave request cancelled successfully");
      setIsCancelDialogOpen(false);
      setLeaveToCancel(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Form submit handlers
  const handleFormSubmit = async (leaveRequest: AdminLeaveRequestPayload) => {
    if (editingLeaveRequestId) {
      await updateLeaveRequest.mutateAsync({
        id: editingLeaveRequestId,
        payload: leaveRequest,
        isAdmin: true,
      });
    } else {
      await createLeaveRequest.mutateAsync({
        payload: leaveRequest,
        isAdmin: true,
      });
    }
    setIsCrudSheetOpen(false);
  };

  // AutoCompleteUsers code
  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  // DATE RANGE
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  useEffect(() => {
    setStartDateFilter(
      dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : undefined
    );

    setEndDateFilter(
      dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined
    );
  }, [dateRange]);

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Leave Requests"
          description="Manage, review, and process employee leave requests."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Leave"
          isLoading={isFetching}
        />

        {canView && (
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          {/* User Filter with Autocomplete */}
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={users}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />

          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Requested", value: "REQUESTED" },
              { label: "Approved", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
              { label: "Cancelled", value: "CANCELLED" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />

          <SelectFilter
            placeholder="Branch"
            options={[
              ...branches.map((branch) => ({
                label: branch.name,
                value: String(branch.id),
              })),
              { label: "All branches", value: "clear" },
            ]}
            value={branchFilter}
            onChange={(value) => {
              setBranchFilter(value);
              // A department belongs to one branch, so a branch change can
              // orphan the narrower filter — drop it rather than send a pair
              // that matches nothing.
              setDepartmentFilter(undefined);
            }}
          />

          <SelectFilter
            placeholder="Department"
            options={[
              ...departments.map((department) => ({
                label: department.name,
                value: String(department.id),
              })),
              { label: "All departments", value: "clear" },
            ]}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />

          <SelectFilter
            placeholder="Approver"
            options={[
              { label: "No one can approve", value: "stuck" },
              { label: "Can't be approved (limit or balance)", value: "blocked" },
              { label: "All", value: "clear" },
            ]}
            value={approverFilter}
            onChange={setApproverFilter}
          />

          {/* selects date in range  */}
          <DateSelector
            value={dateRange}
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(undefined)}
          />
        </FiltersPanel>
        )}

        {canView && (
        <LeaveRequestTable
          leaveRequests={leaveRequests}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onViewHistory={handleViewHistory}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
          canApprove={canApproveOrHasSubordinates}
          onRowClick={(row) =>
            navigate({
              to: "/admin/leave/requests/$userId",
              params: { userId: String(row.user.id) },
            })
          }
        />
        )}
        <LeaveRequestFormSheet
          isOpen={isCrudSheetOpen}
          onClose={() => setIsCrudSheetOpen(false)}
          onSubmit={handleFormSubmit}
          users={users}
          onSearchUsers={handleSearchUsers}
          editingLeaveRequest={selectedLeaveRequest}
          isLoading={isMutating}
        />
        <LeaveRequestDetail
          isOpen={isDetailSheetOpen}
          onClose={handleCloseDetailSheet}
          isLoading={isMutating}
          leaveRequest={selectedLeaveRequest}
          onApprove={handleApprove}
          onReject={handleReject}
          canManage={canManage}
          canApprove={canApproveOrHasSubordinates}
          onCancel={async (id, remarks) => {
            await cancelLeaveRequest.mutateAsync({ id, remarks });
            toast.success("Leave request cancelled successfully");
            handleCloseDetailSheet();
          }}
        />
        {canView && (
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        )}
        <LeaveRequestHistorySheet
          isOpen={isHistorySheetOpen}
          onClose={() => setIsHistorySheetOpen(false)}
          leaveRequest={historyLeaveRequest}
        />
        <CancelWithRemarksDialog
          open={isCancelDialogOpen}
          onOpenChange={setIsCancelDialogOpen}
          onConfirm={handleCancelConfirm}
          isLoading={cancelLeaveRequest.isPending}
          title="Cancel Leave Request"
          description={
            leaveToCancel
              ? `This will cancel the approved ${leaveToCancel.leave.name} request of ${leaveToCancel.user.first_name} ${leaveToCancel.user.last_name} and restore the deducted balance. Please provide a reason if any.`
              : ""
          }
        />
      </div>
    </SidebarInset>
  );
}
